import React, { useRef } from 'react';
import { useColorScheme } from '@mui/material/styles';
import { useItemCards } from './ItemCardContext';
import ItemCardWindow from './ItemCardWindow';
import { resolveIdDisplay, stripIdPrefix } from '../../utils/itemId';
import { statusOf, approvalToneOf } from '../../utils/statusTone';
import { itemCardZ } from '../../utils/zLayers';
import { usePref } from '../../services/prefs';

// Hovering the mini peek expands it to the full card — but only after this
// delay, so a pointer merely passing over the mini on its way elsewhere
// doesn't pop the card open.
const MINI_EXPAND_DELAY = 100;

// Map key standing in for the hover mini in the stacking ranking below. A Symbol
// so it can never collide with a window's `${id}#${z}` key.
const MINI_KEY = Symbol('item-card-mini');

/** Mounted once near the app root: renders the hover mini-peek + all pinned windows. */
export default function ItemCardLayer() {
  const { mini, windows, hideMini, cancelHide, openWindow } = useItemCards();
  const showHumanIds = usePref('showHumanIds');
  const expandTimer = useRef(null);
  const armExpand = () => {
    // #801: the pointer has ARRIVED, so the hide the trigger's mouseleave scheduled must
    // not fire. Without this the peek can be torn away mid-transit whenever the 100ms
    // expand loses to the 140ms hide, which load makes possible.
    cancelHide();
    if (expandTimer.current) clearTimeout(expandTimer.current);
    expandTimer.current = setTimeout(() => openWindow(mini.item, mini.rect, { pinned: false }), MINI_EXPAND_DELAY);
  };
  const disarmExpand = () => {
    if (expandTimer.current) { clearTimeout(expandTimer.current); expandTimer.current = null; }
  };
  // The layer mounts OUTSIDE the AppShell's `.app` wrapper, so re-establish the
  // design-token scope (and dark-mode scheme) here — otherwise var(--base) etc.
  // are undefined and the floating cards render transparent/unstyled.
  const { mode, systemMode } = useColorScheme();
  const resolved = (mode === 'system' ? systemMode : mode) || 'light';

  // #627: paint every floating surface at its RANK within the open set, not at its
  // raw `z`. `ItemCardContext.zRef` is a monotonically incrementing counter — it
  // never resets — so the raw value climbs without bound over a session, and a
  // z-index derived from it eventually pegs at `itemCardZ`'s ceiling. Rank is
  // bounded by the number of surfaces actually open (single digits), so the clamp
  // is a backstop rather than the normal path. Relative stacking is preserved
  // exactly: rank is the ascending order of the same `z`.
  //
  // The MINI is ranked here alongside the windows (adversarial review F5). It used
  // to be passed `itemCardZ(mini.z)` raw, and because `showMini` bumps the counter
  // on EVERY hover, roughly 90 hovers into a session the mini was permanently
  // pinned at Z_ITEM_CARD_MAX — saturated, so it could no longer stack correctly
  // against the windows it is supposed to sit above. One combined ranking keeps the
  // mini above any window opened before it (which is what `showMini`'s bump means)
  // without ever leaving the band.
  //
  // Deliberately NOT done by renumbering `z` in the context: `w.key` is
  // `${id}#${z}`, and every close/pin call is keyed on it, so renumbering would
  // churn React keys and break those lookups. Ordering stays the context's job;
  // only the PAINTED value is bounded here.
  const rankOf = new Map(
    [
      ...windows.map((w) => ({ key: w.key, z: w.z || 0 })),
      ...(mini ? [{ key: MINI_KEY, z: mini.z || 0 }] : []),
    ]
      .sort((a, b) => a.z - b.z)
      .map((s, i) => [s.key, i + 1]),
  );

  let miniNode = null;
  if (mini && mini.rect) {
    const it = mini.item || {};
    const ids = resolveIdDisplay(it, showHumanIds);
    const st = statusOf(it);
    const top = Math.min(mini.rect.bottom + 6, window.innerHeight - 130);
    const left = Math.min(mini.rect.left, window.innerWidth - 300);
    miniNode = (
      <div
        className="ic-mini"
        style={{ top, left, zIndex: itemCardZ(rankOf.get(MINI_KEY) || 1) }}
        // Moving the pointer into the mini expands it to the full card (preview),
        // after a short delay (#UX) so a pass-through hover doesn't pop it open.
        onMouseEnter={armExpand}
        onMouseLeave={() => { disarmExpand(); hideMini(); }}
        onClick={() => { disarmExpand(); openWindow(mini.item, mini.rect, { pinned: true }); }}
        // #793 H: the author's `role="button"` promised a button but delivered a
        // mouse-only one — not tabbable, and Enter/Space did nothing. Kept as a div
        // (`.ic-mini` is a floating CARD whose box model a native <button>'s UA styles
        // would fight) and given the full manual contract instead: focusable, and
        // Enter/Space activate. Space also preventDefaults so the page doesn't scroll.
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          disarmExpand();
          openWindow(mini.item, mini.rect, { pinned: true });
        }}
        role="button"
        aria-label={`Preview ${ids.primary}`}
      >
        <div className="ic-mini-head">
          <span className="idchip" title={ids.secondary || ''}>{stripIdPrefix(ids.primary)}</span>
          {it.type && <span className="badge">{it.type}</span>}
          {/* #801: the same handle the pinned card's status dot now carries — `.ic-dot`
              is shared with the approval dot below, which mounts only when the caller
              stamped a rollup on the seed. */}
          {st.label !== '—' && <span className="ic-dot" data-testid="ic-status-dot" title={`Status: ${st.label}`} style={{ background: st.tone }} />}
          {/* F-21 / REQ-6107 (#581, F2): the approval indicator on the hover mini.
              WP2 deferred this because the mini is a ZERO-network instant preview built
              from a cached seed, and adding a per-hover approval fetch across every
              list/search/graph surface that mounts it is a real perf cost, not a patch.
              Resolved without paying it: the dot renders from `approval_state` when the
              CALLER already holds the bulk-fetched rollup and stamps it on the seed (see
              LinkTypeDrawer). A caller that doesn't shows no dot — the same
              "no entry != pending" rule RequirementsTree.approvalDot already applies —
              so the mini still makes no network call of its own.
              F-23 / REQ-6109: glyph + aria-label, never colour alone. */}
          {it.approval_state && (() => {
            const tone = approvalToneOf(it.approval_state, it.approval_stale_state);
            // F-61 / REQ-6119 (#606, D-10, #629): same annotation as every other
            // tone-based approval dot — the state itself is unchanged (the approval
            // STANDS); this only appends to the text/aria-label channel.
            const label = it.approval_reviewer_inactive ? `${tone.label} · user inactive` : tone.label;
            return (
              <span
                className="ic-dot"
                role="img"
                aria-label={`Approval: ${label}`}
                title={`Approval: ${label}`}
                data-testid="ic-mini-approval-dot"
                style={{
                  background: tone.tone, display: 'inline-flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 7, fontWeight: 900, color: '#fff',
                }}
              >
                {tone.glyph}
              </span>
            );
          })()}
        </div>
        <div className="ic-mini-title">{it.title || 'Untitled'}</div>
        <div className="ic-mini-hint">Click to pin &amp; edit · drag to move</div>
      </div>
    );
  }

  return (
    <div className={`app ${resolved}`} style={{ display: 'contents' }}>
      {miniNode}
      {windows.map((w) => <ItemCardWindow key={w.key} win={w} zRank={rankOf.get(w.key) || 1} />)}
    </div>
  );
}
